import L from 'leaflet';
import type { ParcelBoundary, SaleRecord } from './types';
import { escapeHtml, formatCurrency } from './format';

export const RUTHERFORD_CENTER: L.LatLngExpression = [35.4027, -81.9198];

const MIN_MARKER_RADIUS = 6;
const MAX_MARKER_RADIUS = 20;
const DEFAULT_MARKER_RADIUS = 7;
const SALE_RECENT_RGB = { r: 11, g: 92, b: 255 };
const SALE_OLDER_RGB = { r: 242, g: 79, b: 61 };
const SALE_MISSING_AGE_COLOR = '#9ca3af';
// Keep the rendered marker list outside Leaflet so keyboard/open-from-list flows can reopen
// the same markers after filtering or re-rendering without walking the layer tree.
let renderedMarkers: L.CircleMarker[] = [];
// Fast lookup by sale id lets popup/drilldown code find the live marker instance directly.
let renderedMarkersByRecordId = new Map<string, L.CircleMarker>();
// Tracks the active reveal animation so a newer render can cancel stale frame callbacks.
let markerRevealFrame = 0;

export type SaleSelectionHandler = (record: SaleRecord, marker: L.CircleMarker) => void;
export type MapStyleToggleState = {
  isSatellite: boolean;
  toggleMapStyle: () => void;
};

function saleAgeDays(record: SaleRecord): number | null {
  if (record.saleDateMs === null) return null;
  return Math.max(0, Math.round((Date.now() - record.saleDateMs) / 86_400_000));
}

function colorForAge(ageDays: number | null, maxAgeDays: number): string {
  if (ageDays === null) return SALE_MISSING_AGE_COLOR;
  const ratio = Math.min(1, Math.max(0, ageDays / Math.max(maxAgeDays, 1)));
  // Keep this marker ramp paired with --sale-recent / --sale-older in styles.css.
  const r = Math.round(SALE_RECENT_RGB.r + (SALE_OLDER_RGB.r - SALE_RECENT_RGB.r) * ratio);
  const g = Math.round(SALE_RECENT_RGB.g + (SALE_OLDER_RGB.g - SALE_RECENT_RGB.g) * ratio);
  const b = Math.round(SALE_RECENT_RGB.b + (SALE_OLDER_RGB.b - SALE_RECENT_RGB.b) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
}

function radiusForPrice(price: number | null, min: number, max: number): number {
  if (price === null || max <= 0) return DEFAULT_MARKER_RADIUS;

  const safeMin = Math.max(1, min);
  const safeMax = Math.max(safeMin, max);
  const safePrice = Math.min(safeMax, Math.max(safeMin, price));

  if (safeMax === safeMin) return (MIN_MARKER_RADIUS + MAX_MARKER_RADIUS) / 2;

  const logMin = Math.log1p(safeMin);
  const logMax = Math.log1p(safeMax);
  const normalized = (Math.log1p(safePrice) - logMin) / (logMax - logMin);
  const eased = Math.pow(Math.min(1, Math.max(0, normalized)), 0.9);

  return MIN_MARKER_RADIUS + eased * (MAX_MARKER_RADIUS - MIN_MARKER_RADIUS);
}

export function createSalesMap(): {
  map: L.Map;
  countyBoundaryLayer: L.LayerGroup;
  salesLayer: L.LayerGroup;
  parcelBoundaryLayer: L.LayerGroup;
  mapStyleState: MapStyleToggleState;
} {
  const map = L.map('map', { zoomControl: false }).setView(RUTHERFORD_CENTER, 10);
  L.control.zoom({ position: 'bottomright' }).addTo(map);

  const roads = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19,
    attribution: '&copy; OpenStreetMap contributors',
  });

  const satellite = L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community',
    },
  );

  roads.addTo(map);

  let isSatellite = false;

  function setSatelliteMode(nextSatellite: boolean): void {
    if (nextSatellite === isSatellite) return;
    isSatellite = nextSatellite;
    if (isSatellite) {
      map.removeLayer(roads);
      satellite.addTo(map);
    } else {
      map.removeLayer(satellite);
      roads.addTo(map);
    }
    updateButton();
  }

  let toggleButton: HTMLButtonElement | null = null;

  function updateButton(): void {
    if (!toggleButton) return;
    toggleButton.setAttribute('aria-pressed', String(isSatellite));
    const label = isSatellite ? 'Show roads view' : 'Show satellite view';
    toggleButton.title = label;
    toggleButton.setAttribute('aria-label', label);
    toggleButton.dataset.state = isSatellite ? 'satellite' : 'roads';
  }

  const MapStyleControl = L.Control.extend({
    options: { position: 'bottomright' },
    onAdd() {
      const container = L.DomUtil.create('div', 'leaflet-bar map-style-control');
      toggleButton = L.DomUtil.create('button', 'map-style-toggle', container);
      toggleButton.type = 'button';
      toggleButton.setAttribute('aria-pressed', 'false');
      toggleButton.setAttribute('aria-label', 'Show satellite view');
      toggleButton.addEventListener('click', () => {
        setSatelliteMode(!isSatellite);
      });
      toggleButton.innerHTML = '<span class="map-style-icon" aria-hidden="true"></span>';
      updateButton();
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.disableScrollPropagation(container);
      return container;
    },
  });

  // The base map is intentionally toggleable at runtime so analysts can switch between roads
  // and imagery without losing their current filter state or map extent.
  map.addControl(new MapStyleControl());

  return {
    map,
    countyBoundaryLayer: L.layerGroup().addTo(map),
    salesLayer: L.layerGroup().addTo(map),
    parcelBoundaryLayer: L.layerGroup().addTo(map),
    mapStyleState: {
      get isSatellite() {
        return isSatellite;
      },
      toggleMapStyle() {
        setSatelliteMode(!isSatellite);
      },
    },
  };
}

/**
 * Leaflet stores polygon points as [lat, lng], while the ArcGIS boundary rings arrive as
 * [lng, lat]. This conversion keeps the rendering code aligned with the source geometry shape.
 */
export function boundaryLatLngs(boundary: ParcelBoundary): L.LatLngExpression[][] {
  return boundary.rings.map((ring) => ring.map(([longitude, latitude]) => [latitude, longitude]));
}

export function boundaryBounds(boundary: ParcelBoundary): L.LatLngBounds {
  return L.latLngBounds(boundaryLatLngs(boundary).flat() as L.LatLngExpression[]);
}

export function clearParcelBoundary(parcelBoundaryLayer: L.LayerGroup): void {
  parcelBoundaryLayer.clearLayers();
}

export function renderParcelBoundary(parcelBoundaryLayer: L.LayerGroup, boundary: ParcelBoundary): void {
  parcelBoundaryLayer.clearLayers();
  L.polygon(boundaryLatLngs(boundary), {
    interactive: false,
    color: '#f24f3d',
    fillColor: '#f24f3d',
    fillOpacity: 0.12,
    opacity: 0.95,
    weight: 4,
  }).addTo(parcelBoundaryLayer);
}

export function renderCountyBoundary(countyBoundaryLayer: L.LayerGroup, boundary: ParcelBoundary): void {
  countyBoundaryLayer.clearLayers();
  L.polygon(boundaryLatLngs(boundary), {
    interactive: false,
    color: '#0b5cff',
    fill: false,
    opacity: 0.88,
    weight: 3,
    dashArray: '8 8',
    lineJoin: 'round',
  }).addTo(countyBoundaryLayer);
}

/**
 * Rebuilds the sales overlay after filtering and keeps the active marker identities available
 * for popup and list-driven selection flows.
 */
export function renderSaleMarkers(
  map: L.Map,
  salesLayer: L.LayerGroup,
  records: SaleRecord[],
  onSelect?: SaleSelectionHandler,
  parcelBoundaryLayer?: L.LayerGroup,
  options: { animate?: boolean } = {},
): void {
  salesLayer.clearLayers();
  if (parcelBoundaryLayer) clearParcelBoundary(parcelBoundaryLayer);
  renderedMarkers = [];
  renderedMarkersByRecordId = new Map();
  if (markerRevealFrame !== 0) {
    window.cancelAnimationFrame(markerRevealFrame);
    markerRevealFrame = 0;
  }

  if (records.length === 0) return;

  const prices = records.map((record) => record.price).filter((price): price is number => price !== null);
  const min = prices.length ? Math.min(...prices) : 0;
  const max = prices.length ? Math.max(...prices) : 0;
  const maxAge = Math.max(...records.map((record) => saleAgeDays(record) ?? 0), 1);
  const bounds = L.latLngBounds([]);
  const shouldAnimate = options.animate !== false;

  for (const record of records) {
    const age = saleAgeDays(record);
    const marker = L.circleMarker([record.latitude, record.longitude], {
      radius: radiusForPrice(record.price, min, max),
      color: colorForAge(age, maxAge),
      fillColor: colorForAge(age, maxAge),
      fillOpacity: shouldAnimate ? 0 : 0.72,
      opacity: shouldAnimate ? 0 : 0.98,
      weight: 3,
      className: 'sale-marker',
      bubblingMouseEvents: false,
    });
    marker.bindTooltip(`<strong>${escapeHtml(record.address)}</strong><br>${escapeHtml(formatCurrency(record.price))}`);
    marker.on('click', () => {
      onSelect?.(record, marker);
    });
    marker.addTo(salesLayer);
    renderedMarkers.push(marker);
    renderedMarkersByRecordId.set(record.id, marker);
    bounds.extend([record.latitude, record.longitude]);
  }

  if (bounds.isValid()) map.fitBounds(bounds.pad(0.12), { maxZoom: 14, animate: false });

  if (!shouldAnimate || renderedMarkers.length === 0) return;

  const revealFrame = ++markerRevealFrame;
  window.requestAnimationFrame(() => {
    if (revealFrame !== markerRevealFrame) return;

    // The small stagger keeps large result sets from popping in all at once and makes the
    // refresh feel deliberate without changing which markers are present.
    renderedMarkers.forEach((marker, index) => {
      const element = marker.getElement() as SVGElement | null;
      if (element) {
        const stagger = Math.min(240, (index % 10) * 18 + Math.random() * 50);
        element.style.transitionDelay = `${stagger}ms`;
      }
      marker.setStyle({ fillOpacity: 0.72, opacity: 0.98 });
    });
  });
}

/**
 * Opens the marker that corresponds to the current result ordering so keyboard and list
 * navigation can reuse the same popup behavior as direct clicks.
 */
export function openRenderedMarker(index: number): boolean {
  const marker = renderedMarkers[index];
  if (!marker) return false;

  marker.fire('click');
  return true;
}

/**
 * Returns the live marker instance for a sale record so external UI code can coordinate map
 * state with a specific result row.
 */
export function renderedMarkerForRecord(recordId: string): L.CircleMarker | null {
  return renderedMarkersByRecordId.get(recordId) ?? null;
}
